import { supabase } from "../supabase";
import { getAuthRedirectUrl } from "../helpers/authHelpers";
import { SERVICE_GMAIL } from "./registry";

/**
 * @typedef {object} ConnectionStatus
 * @property {string} serviceId
 * @property {boolean} connected
 * @property {string|null} accountEmail
 * @property {string|null} status
 * @property {string|null} connectedAt
 */

function invokeErrorMessage(error, data) {
  if (data?.error) {
    return String(data.error);
  }
  if (error?.message) {
    return error.message;
  }
  return "Connected Services request failed";
}

/**
 * List all known Connected Services with connection status (no tokens).
 * @returns {Promise<ConnectionStatus[]>}
 */
export async function listConnectedServices() {
  const { data, error } = await supabase.functions.invoke("connected-services", {
    body: { action: "list" },
  });

  if (error || data?.error) {
    throw new Error(invokeErrorMessage(error, data));
  }

  return Array.isArray(data?.services) ? data.services : [];
}

/**
 * @param {string} serviceId
 * @returns {Promise<ConnectionStatus>}
 */
export async function getConnectedServiceStatus(serviceId) {
  const { data, error } = await supabase.functions.invoke("connected-services", {
    body: { action: "status", serviceId },
  });

  if (error || data?.error) {
    const code = data?.code;
    if (code === "unknown_service") {
      return {
        serviceId,
        connected: false,
        accountEmail: null,
        status: null,
        connectedAt: null,
      };
    }
    throw new Error(invokeErrorMessage(error, data));
  }

  return (
    data?.connection || {
      serviceId,
      connected: false,
      accountEmail: null,
      status: null,
      connectedAt: null,
    }
  );
}

/**
 * Starts provider OAuth. Returns the authorization URL (tokens stay on the server).
 * @param {string} [serviceId]
 * @returns {Promise<{ authUrl: string, serviceId: string }>}
 */
export async function startConnectedServiceOAuth(serviceId = SERVICE_GMAIL) {
  const { data, error } = await supabase.functions.invoke("connected-services", {
    body: {
      action: "oauth_start",
      serviceId,
      returnOrigin: getAuthRedirectUrl(),
    },
  });

  if (error || data?.error || !data?.authUrl) {
    throw new Error(invokeErrorMessage(error, data));
  }

  return { authUrl: data.authUrl, serviceId: data.serviceId || serviceId };
}

/**
 * @param {string} serviceId
 * @returns {Promise<ConnectionStatus>}
 */
export async function disconnectConnectedService(serviceId) {
  const { data, error } = await supabase.functions.invoke("connected-services", {
    body: {
      action: "disconnect",
      serviceId,
    },
  });

  if (error || data?.error) {
    throw new Error(invokeErrorMessage(error, data));
  }

  return (
    data?.connection || {
      serviceId,
      connected: false,
      accountEmail: null,
      status: null,
      connectedAt: null,
    }
  );
}
